import { useEffect, useState, useSyncExternalStore } from "react";
import type { User } from "@supabase/supabase-js";
import { sendWelcomeEmail } from "@/lib/welcome.functions";
import { hasStoredSession } from "@/lib/has-stored-session";

// Detect whether we just landed back from an email-confirmation link.
// Supabase appends `type=signup` to the redirect URL hash after verifying.
function isSignupConfirmation(): boolean {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash || "";
  return /[#&]type=signup\b/.test(hash);
}

/**
 * Διαβάζει ΣΥΓΧΡΟΝΑ τον χρήστη από το αποθηκευμένο session του Supabase στο
 * localStorage, ώστε το UI (π.χ. το όνομα στο header, τα πεδία του
 * λογαριασμού) να εμφανίζεται αμέσως χωρίς να περιμένει το lazy chunk του
 * supabase-js. Ο πραγματικός έλεγχος γίνεται κανονικά από τον server σε κάθε
 * αίτημα, οπότε αυτό είναι μόνο οπτική βελτιστοποίηση.
 */
function readCachedUser(): User | null {
  if (typeof window === "undefined") return null;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const json = raw.startsWith("base64-")
        ? atob(raw.slice("base64-".length))
        : raw;
      const parsed = JSON.parse(json) as { user?: User; expires_at?: number };
      if (!parsed?.user) continue;
      // Ληγμένο token → μην εμφανίζεις ψεύτικη σύνδεση.
      if (parsed.expires_at && parsed.expires_at * 1000 < Date.now() - 60_000) continue;
      return parsed.user;
    }
  } catch {}
  return null;
}

// ---------------------------------------------------------------------------
// Κοινό (singleton) auth store: ένας μόνο listener/φόρτωση του supabase-js για
// όλη την εφαρμογή. Χωρίς αυτό, κάθε component που καλεί useAuth ξεκινούσε τη
// δική του φόρτωση και ξεκινούσε από `null`, με αποτέλεσμα το header να
// γράφει «Σύνδεση» σε κάθε αλλαγή σελίδας μέχρι να απαντήσει το Supabase.
// ---------------------------------------------------------------------------
type AuthState = { user: User | null; loading: boolean };

let state: AuthState = { user: null, loading: true };
const serverState: AuthState = { user: null, loading: true };
const listeners = new Set<() => void>();
let started = false;
let hydrated = false;

function setState(next: AuthState) {
  if (state.user === next.user && state.loading === next.loading) return;
  state = next;
  for (const l of listeners) l();
}

function hydrateFromStorage() {
  if (hydrated || typeof window === "undefined") return;
  hydrated = true;
  const cached = readCachedUser();
  if (cached) setState({ user: cached, loading: false });
  else if (!hasStoredSession()) setState({ user: null, loading: false });
}

function startAuth() {
  if (started || typeof window === "undefined") return;
  started = true;

  const justConfirmed = isSignupConfirmation();
  let welcomeTriggered = false;
  const maybeSendWelcome = () => {
    if (!justConfirmed || welcomeTriggered) return;
    welcomeTriggered = true;
    sendWelcomeEmail().catch(() => {});
  };

  const load = () => {
    import("@/integrations/supabase/client").then(({ supabase }) => {
      supabase.auth.onAuthStateChange((_event, session) => {
        setState({ user: session?.user ?? null, loading: false });
        if (session?.user) maybeSendWelcome();
      });
      supabase.auth.getSession().then(async ({ data }) => {
        setState({ user: data.session?.user ?? null, loading: false });
        if (!data.session?.user) return;
        maybeSendWelcome();
        // Το αποθηκευμένο token μπορεί να έχει ακυρωθεί (π.χ. rotation του
        // κλειδιού υπογραφής). Τότε το UI έδειχνε «συνδεδεμένος» ενώ κάθε
        // κλήση στον server αποτύγχανε. Επαληθεύουμε μία φορά και, αν το
        // token δεν γίνεται δεκτό, καθαρίζουμε τη συνεδρία τοπικά.
        try {
          const { error } = await supabase.auth.getUser();
          if (!error) return;
          const msg = `${error.message ?? ""}`.toLowerCase();
          const status = (error as { status?: number }).status;
          const invalid =
            status === 401 ||
            status === 403 ||
            msg.includes("jwt") ||
            msg.includes("kid") ||
            msg.includes("session");
          if (!invalid) return;
          await supabase.auth.signOut({ scope: "local" }).catch(() => {});
          setState({ user: null, loading: false });
        } catch {}
      });
    });
  };

  if (hasStoredSession()) {
    load();
    return;
  }
  // Ανώνυμος επισκέπτης: δεν κατεβάζουμε καθόλου το chunk του Supabase στην
  // αρχική φόρτωση — μόνο μετά από αλληλεπίδραση ή ως δίχτυ ασφαλείας.
  const events = ["pointerdown", "keydown", "touchstart"] as const;
  let idleId: number | undefined;
  const kick = () => {
    for (const ev of events) window.removeEventListener(ev, kick);
    if (idleId !== undefined) window.clearTimeout(idleId);
    load();
  };
  for (const ev of events) window.addEventListener(ev, kick, { once: true, passive: true });
  idleId = window.setTimeout(kick, 30000);
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useAuth() {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => state,
    () => serverState,
  );
  const [, force] = useState(0);

  useEffect(() => {
    hydrateFromStorage();
    startAuth();
    force((n) => n + 1);
  }, []);

  return { user: snapshot.user, loading: snapshot.loading };
}
